import { WeaponAmmo } from "game-common/character/character"
import { EntityManager } from "game-common/entity_manager"
import { resolveItemTypeToMeta } from "game-common/item/item"
import { TileEffect } from "game-common/map/map"
import { Mechanics } from "game-common/mechanics/mechanics"
import { Bounds, Coordinate, Entity, EntityDimensions, EntityId, Message, TileSize } from "game-common/models"
import { blockedStop, isOpenGroundForArrow, reachPoint, spentArrowLasts, spentArrowPose } from "game-common/spent_arrow_landing"
import { TileMapMetaProvider } from "game-common/tile_map_meta_provider"

/**
 * THE SHOOTER'S OWN ARROW COMING DOWN, drawn here as its flight ends -- in the turf, at a blocker, or in a body.
 *
 * What a spent arrow leaves is the server's to decide (server/src/combat/spent_arrows.ts), and its word
 * reaches the shooter a round trip after the arrow on their screen has stopped: about 100ms on a good
 * connection, and on a 300ms one the arrow vanished and the dirt puff and the arrow in the turf turned up a
 * third of a second later. This client already flies the arrow itself (ClientGameLogic.predictBullet), and
 * the landing is worked out from what both sides share -- the arrow's id, its line, the map
 * (common/spent_arrow_landing.ts) -- so it draws the landing too, under the id the server gives its own
 * (`landed-<arrow id>`). When the server's arrives it lands on this one, in the same place and pose, and
 * takes it over.
 *
 * An arrow that meets an enemy ends in it there and then (strikes), and nothing lands: a hit leaves nothing
 * on the ground. The hit itself -- the damage, the flash -- is still the server's, and arrives when it does.
 * One that only passes near something a shot can hit waits for the server, as every landing used to: this
 * client cannot be sure of a body it is drawing a little in the past. Where the arrow comes down is worked
 * out the same way on both sides, whatever either side's step: exactly at the end of its reach, or exactly
 * at the blocker that stopped it.
 *
 * A landing the server never confirms is taken away again (CONFIRM_MS): the server saw a hit this client
 * did not, or turned the shot down. It goes without the destroyed-entity lag buffer, so the server's own,
 * if it is merely late, still lands.
 */

// Slack around a body, for one drawn a little in the past or a little to one side of where the server has it.
const CONTEST_MARGIN_PX = 12
// How long a drawn landing waits for the server's before it is taken back: a round trip, and then some.
const CONFIRM_MS = 1500
// How long a landing is remembered, so the server's late impact for it is still recognised and skipped.
const RECORD_MS = 4000

export interface LandingHost {
    entities: () => EntityManager<Entity>
    map: () => TileMapMetaProvider | undefined
    /** This client's player: who a body is friend or foe to. */
    shooter: () => Entity | undefined
    /** One of this player's camp (the ember roster): the server lets their arrows through them. */
    isCampMember: (entityId: EntityId) => boolean
    createEntity: (message: Message) => Entity | undefined
    tileEffect: (effect: TileEffect) => void
    /** Take an entity off the scene without the lag buffer that would refuse the server's of the same id. */
    discard: (entity: Entity) => void
}

// What the landing needs from the shot, taken as it leaves the bow: the server's reports of the arrow are
// written over it as they arrive (its flags among them), and they are a round trip old.
interface Flight {
    ammo?: string
    origin: Coordinate
    speed2: Coordinate
    maxDistance?: number
    dimensions: Entity["dimensions"]
    spriteId: string
    heading: number
    shooterId: EntityId
    roomId: EntityId
}

interface Landing {
    landedAt: number
    standInId?: EntityId
    confirmed: boolean
}

export const landedArrowId = (arrowId: EntityId) => `landed-${arrowId}`

export class PredictedLandings {
    // Arrows whose flight has passed near a body: the server may have struck it, and then nothing lands.
    private contested = new Set<EntityId>()
    private flights = new Map<EntityId, Flight>()
    private landings = new Map<EntityId, Landing>()

    constructor(private readonly host: LandingHost) {}

    /** This client has just loosed `bullet` and is drawing it itself. */
    launch = (bullet: Entity) => {
        if (!bullet.origin) return
        this.flights.set(bullet.entityId, {
            ammo: WeaponAmmo[bullet.flags?.originWeaponType], origin: { ...bullet.origin }, speed2: { ...bullet.speed2 },
            maxDistance: bullet.maxDistance, dimensions: { ...bullet.dimensions }, spriteId: bullet.spriteId,
            heading: bullet.movement.angle, shooterId: bullet.ownerEntityId,
            roomId: bullet.roomId,
        })
    }

    /**
     * Whether this client's own arrow has, this frame, met a body the server will strike: something
     * strikeable that is not another player, not one of this player's camp, and not friendly to them by
     * the server's own rule (Mechanics.social.computeDisposition). The arrow ends there, on this screen,
     * a round trip before the server's word on the hit, which still decides the damage. If the server
     * lets it through after all, its own arrow comes down later at the end of the reach, as before.
     */
    strikes = (bullet: Entity): boolean => {
        const flight = this.flights.get(bullet.entityId)
        if (!flight) return false
        const arrow = bullet.getCollisionBounds()
        const struck = (other: Entity) => other.entityId !== flight.shooterId && !!other.strikeable && !other.isPlayer() &&
            !other.isDead() && other.visible !== false && !!other.location && !this.isFriend(other) &&
            arrow.collision(this.boundsOf(other))
        if (!this.host.entities().find(struck, "npc")) return false
        this.flights.delete(bullet.entityId)
        this.contested.delete(bullet.entityId)
        return true
    }

    /** A frame of this client's own arrow in flight. */
    follow = (bullet: Entity) => {
        if (!this.contested.has(bullet.entityId) && this.nearBody(bullet)) this.contested.add(bullet.entityId)
    }

    /** This client's own arrow has stopped. The server ending it (a hit, a halt) is not a landing to draw. */
    land = (bullet: Entity, now: number) => {
        const arrowId = bullet.entityId
        const contested = this.contested.delete(arrowId)
        const flight = this.flights.get(arrowId)
        this.flights.delete(arrowId)
        if (!flight || contested || bullet.isDead() || this.landings.has(arrowId)) return
        if (flight.ammo !== "arrow") return
        // A stalled frame can hand over the server's arrow before this one's flight is played out.
        if (this.host.entities().get(landedArrowId(arrowId))) return
        const end = flight.maxDistance !== undefined &&
            Math.hypot(bullet.location.x - flight.origin.x, bullet.location.y - flight.origin.y) > flight.maxDistance
            ? "reach" : "blocked"
        const at = end === "reach" ? reachPoint(flight) : blockedStop(flight, this.host.map())
        if (!at) return

        const { heading, roomId } = flight
        const pose = spentArrowPose({ bullet, shooterId: flight.shooterId, at, heading, end })
        const landing: Landing = { landedAt: now, confirmed: false }
        this.landings.set(arrowId, landing)
        // Water or a wall: it is lost, and the server leaves nothing either.
        if (!isOpenGroundForArrow(this.host.map(), pose.at)) return

        // As leaveSpentArrow plays them, less the id the server tags its own with. A blocker plays its own
        // strike, which is still the server's.
        const impactPosition = { roomId, x: pose.at.x, y: pose.at.y }
        const mapRoughCoordinates = { x: Math.floor(pose.at.x / TileSize), y: Math.floor(pose.at.y / TileSize) }
        if (end === "reach") this.host.tileEffect({ effectType: "arrow-impact", impactPosition, impactAngle: heading,
            strikeStrength: .6, mapRoughCoordinates })
        if (!spentArrowLasts(arrowId)) {
            this.host.tileEffect({ effectType: "arrow-broken", impactPosition, impactAngle: pose.heading,
                impactPitch: pose.pitch, mapRoughCoordinates })
            return
        }

        // The entity message the server sends for its arrow, as far as drawing it goes. Not clickable until
        // it is the server's: there is nothing on the server to pick up yet.
        const arrow = resolveItemTypeToMeta("arrow")
        const standIn = this.host.createEntity({
            entityId: landedArrowId(arrowId), entityType: "npc", npcType: "collectable",
            spriteId: arrow.spriteId || `${arrow.type}-${arrow.color}.png`, noGlint: false, entityState: "live",
            position: [pose.at.x, pose.at.y], angle: pose.heading, direction: "Up", roomId,
            clickable: false, dimensions: { w: TileSize, h: TileSize }, quantity: 1,
            landedArrow: true, landedArrowPitch: pose.pitch || undefined,
        } as Message)
        if (!standIn) return
        standIn.quantity = 1
        landing.standInId = standIn.entityId
    }

    /** Any entity message from the server: the server's own arrow for a landing drawn here confirms it. */
    noteServerEntity = (entityId: EntityId) => {
        if (!entityId?.startsWith("landed-")) return
        const landing = this.landings.get(entityId.slice("landed-".length))
        if (landing) landing.confirmed = true
    }

    /** True for the server's impact or break of an arrow whose landing was already drawn here. */
    skipsEffect = (effect: TileEffect): boolean => {
        const landing = effect.arrowId ? this.landings.get(effect.arrowId) : undefined
        if (!landing) return false
        // A breaking arrow leaves no entity, so this is the server's word on it.
        landing.confirmed = true
        return true
    }

    sweep = (now: number) => {
        this.landings.forEach((landing, arrowId) => {
            if (!landing.confirmed && landing.standInId && now - landing.landedAt > CONFIRM_MS) {
                const standIn = this.host.entities().get(landing.standInId)
                if (standIn) this.host.discard(standIn)
                landing.standInId = undefined
            }
            if (now - landing.landedAt > RECORD_MS) this.landings.delete(arrowId)
        })
        this.contested.forEach(arrowId => {
            if (!this.host.entities().get(arrowId)) this.contested.delete(arrowId)
        })
        this.flights.forEach((_flight, arrowId) => {
            if (!this.host.entities().get(arrowId)) this.flights.delete(arrowId)
        })
    }

    reset = () => {
        this.contested.clear()
        this.flights.clear()
        this.landings.clear()
    }

    // As computeStruck asks it: the shooter's view of the body. Neutral is struck too; only a friend is passed.
    private isFriend = (other: Entity): boolean => {
        if (this.host.isCampMember(other.entityId)) return true
        const shooter = this.host.shooter()
        const disposition = shooter ? Mechanics.social.computeDisposition(shooter, other) : "neutral"
        return disposition !== "hostile" && disposition !== "neutral"
    }

    // Not getCollisionBounds on an entity with no dimensions: a brief update leaves one so, and that throws.
    private boundsOf = (other: Entity): Bounds => {
        if (other.dimensions) return other.getCollisionBounds()
        const size = EntityDimensions[other.entityType] ?? { w: TileSize, h: TileSize }
        return Bounds.from(other.location.x - size.w / 2, other.location.y - size.h / 2, size.w, size.h)
    }

    private nearBody = (bullet: Entity): boolean => {
        const { x, y } = bullet.location
        // Only what a shot can hit: anything the server marks strikeable, and other players. An arrow
        // passes through the rest -- a camp's markers, its fire, a friend -- and still comes down.
        const near = (other: Entity) => {
            if (other.entityId === bullet.ownerEntityId || !(other.strikeable || other.isPlayer()) || other.isDead() ||
                other.visible === false || !other.location || (!other.isPlayer() && this.isFriend(other))) return false
            // Not getCollisionBounds: a brief update leaves an entity here with no dimensions, and that throws.
            const size = other.dimensions ?? EntityDimensions[other.entityType] ?? { w: TileSize, h: TileSize }
            return Math.abs(x - other.location.x) < size.w / 2 + CONTEST_MARGIN_PX &&
                Math.abs(y - other.location.y) < size.h / 2 + CONTEST_MARGIN_PX
        }
        const entities = this.host.entities()
        return !!entities.find(near, "npc") || !!entities.find(near, "player")
    }
}
